import {
  ChangeDetectionStrategy, Component, EventEmitter, Output, signal, inject, OnInit,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { AdminService, AdminForbiddenError } from '../../services/admin.service.js';
import type { AuditLogEntry } from '../../models/admin.types.js';

@Component({
  selector: 'app-admin-audit-log',
  standalone: true,
  imports: [CommonModule],
  changeDetection: ChangeDetectionStrategy.OnPush,
  template: `
    <div class="p-6 space-y-4">
      <!-- Filter chips -->
      <div class="flex flex-wrap items-end gap-2">
        <div class="flex flex-col">
          <label class="text-xs text-gray-300 uppercase tracking-wider font-semibold mb-1">Actor uid</label>
          <input type="text" [value]="actorFilter()" (input)="actorFilter.set($any($event.target).value)"
                 placeholder="uid" class="bg-gray-950 border border-gray-800 rounded px-2 py-1 text-xs text-gray-100 focus:border-purple-500 outline-none" />
        </div>
        <div class="flex flex-col">
          <label class="text-xs text-gray-300 uppercase tracking-wider font-semibold mb-1">Action</label>
          <select [value]="actionFilter()" (change)="actionFilter.set($any($event.target).value)"
                  class="bg-gray-950 border border-gray-800 rounded px-2 py-1 text-xs text-gray-100 focus:border-purple-500 outline-none">
            <option value="">все</option>
            <option value="set_pro">set_pro</option>
            <option value="set_role">set_role</option>
            <option value="refresh_claims">refresh_claims</option>
            <option value="soft_delete_user">soft_delete_user</option>
            <option value="broadcast_banner">broadcast_banner</option>
            <option value="reset_quota">reset_quota</option>
            <option value="set_quota_override">set_quota_override</option>
            <option value="set_quota_defaults">set_quota_defaults</option>
          </select>
        </div>
        <div class="flex flex-col">
          <label class="text-xs text-gray-300 uppercase tracking-wider font-semibold mb-1">Target uid</label>
          <input type="text" [value]="targetFilter()" (input)="targetFilter.set($any($event.target).value)"
                 placeholder="uid" class="bg-gray-950 border border-gray-800 rounded px-2 py-1 text-xs text-gray-100 focus:border-purple-500 outline-none" />
        </div>
        <button (click)="reload(0)" [disabled]="loading()"
                class="px-4 py-1.5 bg-purple-600/30 hover:bg-purple-600/50 disabled:opacity-50 text-purple-200 border border-purple-700/50 rounded text-xs font-semibold">
          Применить
        </button>
        <button (click)="resetFilters()" class="px-3 py-1.5 bg-gray-800 hover:bg-gray-700 text-gray-300 border border-gray-700 rounded text-xs">Сбросить</button>
      </div>

      @if (error()) {
        <div class="p-3 bg-red-950/40 border border-red-900/60 rounded text-sm text-red-300">{{ error() }}</div>
      }

      <div class="bg-gray-950 border border-gray-800 rounded-lg overflow-hidden">
        <table class="w-full text-xs">
          <thead class="bg-gray-900 text-gray-200 uppercase tracking-wider font-semibold">
            <tr>
              <th class="text-left px-3 py-2 font-medium">Когда</th>
              <th class="text-left px-3 py-2 font-medium">Actor</th>
              <th class="text-left px-3 py-2 font-medium">Action</th>
              <th class="text-left px-3 py-2 font-medium">Target</th>
              <th class="text-left px-3 py-2 font-medium">Payload</th>
              <th class="text-left px-3 py-2 font-medium">IP</th>
            </tr>
          </thead>
          <tbody class="divide-y divide-gray-900">
            @for (e of entries(); track e.id) {
              <tr class="hover:bg-gray-900/40">
                <td class="px-3 py-2 text-gray-500 font-mono whitespace-nowrap">{{ formatDate(e.created_at) }}</td>
                <td class="px-3 py-2 text-gray-300 truncate max-w-[180px]" [title]="e.actor_uid || ''">{{ e.actor_email || e.actor_uid || '—' }}</td>
                <td class="px-3 py-2"><span class="px-1.5 py-0.5 bg-gray-900 border border-gray-800 rounded font-mono text-gray-300">{{ e.action }}</span></td>
                <td class="px-3 py-2 text-gray-400 font-mono truncate max-w-[200px]">{{ e.target_uid || '—' }}</td>
                <td class="px-3 py-2 text-gray-500 font-mono truncate max-w-[300px]">{{ formatPayload(e.payload) }}</td>
                <td class="px-3 py-2 text-gray-600 font-mono">{{ e.ip || '—' }}</td>
              </tr>
            } @empty {
              <tr><td colspan="6" class="px-3 py-8 text-center text-gray-500 italic">Журнал пуст</td></tr>
            }
          </tbody>
        </table>
      </div>

      @if (total() > limit()) {
        <div class="flex items-center justify-between text-xs text-gray-500">
          <span>{{ entries().length }} из {{ total() }}</span>
          <div class="flex gap-2">
            <button (click)="reload(offset() - limit())" [disabled]="offset() === 0 || loading()" class="px-3 py-1 bg-gray-800 hover:bg-gray-700 disabled:opacity-50 rounded">←</button>
            <button (click)="reload(offset() + limit())" [disabled]="offset() + limit() >= total() || loading()" class="px-3 py-1 bg-gray-800 hover:bg-gray-700 disabled:opacity-50 rounded">→</button>
          </div>
        </div>
      }
    </div>
  `,
})
export class AdminAuditLogComponent implements OnInit {
  @Output() forbidden = new EventEmitter<void>();
  private adminService = inject(AdminService);

  entries = signal<AuditLogEntry[]>([]);
  total = signal(0);
  offset = signal(0);
  limit = signal(50);
  loading = signal(false);
  error = signal<string | null>(null);

  actorFilter = signal('');
  actionFilter = signal('');
  targetFilter = signal('');

  ngOnInit() {
    this.reload(0);
  }

  async reload(offset: number) {
    this.loading.set(true);
    this.error.set(null);
    try {
      const r = await this.adminService.getAuditLog({
        actor: this.actorFilter() || undefined,
        action: this.actionFilter() || undefined,
        target: this.targetFilter() || undefined,
        limit: String(this.limit()),
        offset: String(offset),
      });
      this.entries.set(r.entries);
      this.total.set(r.total);
      this.offset.set(r.offset);
    } catch (err: any) {
      if (err instanceof AdminForbiddenError) { this.forbidden.emit(); return; }
      this.error.set(err?.message || 'Ошибка загрузки журнала');
    } finally {
      this.loading.set(false);
    }
  }

  resetFilters() {
    this.actorFilter.set('');
    this.actionFilter.set('');
    this.targetFilter.set('');
    this.reload(0);
  }

  formatDate(iso: string): string {
    try { return new Date(iso).toLocaleString('ru-RU', { dateStyle: 'short', timeStyle: 'medium' }); }
    catch { return iso; }
  }

  formatPayload(p: any): string {
    if (!p) return '—';
    try { return JSON.stringify(p); } catch { return String(p); }
  }
}
